import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import "../../styles/index.css";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import BASE_URL from "../config/appconfig";
import { generateAdminExportPdf } from "../pdfFormat/AdminExportPdf";
import SnackbarAlert from "../snackbarAlert/snackbarAlert";

const computeSections = (responses) => {
  const sectionMap = {};
  const sectionOrder = [];
  responses.forEach((r) => {
    const name = r.section?.name;
    if (!name) return;
    const opt = r.question?.options?.find(
      (o) => o._id === r.objective?.selected_option_id
    );
    if (!opt) return;
    if (!sectionMap[name]) {
      sectionMap[name] = { values: [] };
      sectionOrder.push(name);
    }
    sectionMap[name].values.push(opt.option_value);
  });
  return sectionOrder.map((name) => {
    const vals = sectionMap[name].values;
    const score = parseFloat(
      (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)
    );
    return { name, score };
  });
};

const STATUS_COLORS = {
  verified: { bg: "#d8f3ee", color: "#1b8770" },
  pending: { bg: "#ffe9c7", color: "#e69100" },
  rejected: { bg: "#ffd6d6", color: "#d32f2f" },
};

const NewUsers = ({ data, onEdit, onDelete, onPaymentStatusChange, searchQuery = "" }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [sortCol, setSortCol] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");
  const [exportingId, setExportingId] = React.useState(null);
  const [paymentUpdatingId, setPaymentUpdatingId] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info",
  });
  const rowsPerPage = 10;

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  const handlePaymentChange = async (id, newStatus) => {
    if (!onPaymentStatusChange) return;
    setPaymentUpdatingId(id);
    try {
      await onPaymentStatusChange(id, newStatus);
    } finally {
      setPaymentUpdatingId(null);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(0);
  };

  const SortIcon = ({ col }) => {
    const isActive = sortCol === col;
    const Icon = isActive
      ? sortDir === "asc"
        ? ArrowUpwardIcon
        : ArrowDownwardIcon
      : SwapVertIcon;
    return (
      <Icon
        onClick={() => handleSort(col)}
        sx={{
          fontSize: 14,
          cursor: "pointer",
          flexShrink: 0,
          color: isActive ? "#ef2d2d" : "#b0bac4",
          transition: "color 0.15s",
        }}
      />
    );
  };

  const filtered = React.useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    let result = q
      ? data.filter((r) =>
          [r.name, r.email, r.church, r.age, r.location].some((v) =>
            v?.toLowerCase().includes(q)
          )
        )
      : [...data];

    if (sortCol) {
      result.sort((a, b) => {
        const av = (a[sortCol] ?? "").toString().toLowerCase();
        const bv = (b[sortCol] ?? "").toString().toLowerCase();
        const num_a = parseFloat(av),
          num_b = parseFloat(bv);
        const cmp =
          !isNaN(num_a) && !isNaN(num_b) ? num_a - num_b : av.localeCompare(bv);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return result;
  }, [data, searchQuery, sortCol, sortDir]);

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 0));
  const handleNext = () => {
    if ((page + 1) * rowsPerPage < filtered.length) setPage((prev) => prev + 1);
  };
  const open = Boolean(anchorEl);
  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleExport = async (row) => {
    handleMenuClose();
    setExportingId(row.id);

    try {
      const asmRes = await fetch(
        `${BASE_URL}/api/assessments/by-person/${row.id}`
      );
      const asmJson = await asmRes.json();

      if (!asmJson.success || !asmJson.data) {
        showSnackbar("No Framework record found for this user.", "warning");
        return;
      }

      const assessmentId = asmJson.data._id;

      const [respRes, spiderRes] = await Promise.all([
        fetch(`${BASE_URL}/api/response/${assessmentId}`),
        fetch(`${BASE_URL}/api/assessments/${assessmentId}/spider-chart`),
      ]);

      const respJson = await respRes.json();
      const spiderJson = await spiderRes.json();

      const responses = respJson.success ? respJson.data : [];

      if (responses.length === 0) {
        showSnackbar(
          "This user has not submitted any responses yet.",
          "warning"
        );
        return;
      }

      const spiderLabels = spiderJson.success ? spiderJson.labels : [];
      const spiderValues = spiderJson.success ? spiderJson.values : [];

      const sections = computeSections(responses);

      generateAdminExportPdf(
        row,
        sections,
        spiderLabels,
        spiderValues,
        responses
      );

      showSnackbar(`Report exported for ${row.name}`, "success");
    } catch (err) {
      console.error("Export failed:", err);
      showSnackbar("Export failed. Please try again.", "error");
    } finally {
      setExportingId(null);
    }
  };

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const ActionMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "center", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            ml: 2,
            minWidth: 150,
            borderRadius: "8px",
            py: 1,
            fontFamily: "Gotham",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          onEdit(selectedRow);
          handleMenuClose();
        }}
        sx={{ fontSize: 12 }}
      >
        <ListItemIcon>
          <CheckBoxIcon sx={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText><Typography fontSize={12}>Admin Access</Typography></ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          onDelete(selectedRow?.id);
          handleMenuClose();
        }}
        sx={{ fontSize: 12 }}
      >
        <ListItemIcon>
          <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText><Typography fontSize={12}>Delete</Typography></ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleExport(selectedRow)} sx={{ fontSize: 12 }}>
        <ListItemIcon>
          <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText><Typography fontSize={12}>Export Report</Typography></ListItemText>
      </MenuItem>
    </Menu>
  );

  const hCell = {
    fontSize: 12,
    fontWeight: 600,
    color: "#4a5568",
    whiteSpace: "nowrap",
    px: 1.5,
    py: 1.2,
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e2e8f0",
  };

  const bCell = {
    px: 1.5,
    py: 1,
    borderBottom: "1px solid #edf2f7",
    overflow: "hidden",
  };
  const txt = {
    fontSize: 12,
    color: "#4a5568",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ overflowX: "auto" }}
      >
        <Table size="small" sx={{ minWidth: 760, tableLayout: "auto" }}>
          <TableHead>
            <TableRow>
              {[
                { label: "Name", col: "name" },
                { label: "Church", col: "church" },
                { label: "Church Age", col: "age" },
                { label: "Location", col: "location" },
                { label: "Participated", col: "participated" },
                { label: "Connected", col: "connected" },
                { label: "Updates", col: "updates" },
                { label: "Payment", col: "payment_status" },
              ].map(({ label, col }) => (
                <TableCell key={col} sx={hCell}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {label} <SortIcon col={col} />
                  </Box>
                </TableCell>
              ))}
              <TableCell sx={{ ...hCell, width: 40 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                    {searchQuery
                      ? "No matching results found"
                      : "No data available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => (
                <TableRow key={row.id} hover>
                  {/* Name + email */}
                  <TableCell sx={{ ...bCell, minWidth: 110 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        sx={{ ...txt, fontWeight: 600, color: "#2d3748" }}
                      >
                        {row.name}
                      </Typography>
                      {row.is_admin && (
                        <Box
                          sx={{
                            backgroundColor: "#a7e0f1",
                            color: "#009CCC",
                            fontSize: "9px",
                            px: 0.5,
                            py: "1px",
                            borderRadius: "4px",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          ADMIN
                        </Box>
                      )}
                    </Box>
                    <Typography
                      sx={{ ...txt, fontSize: 11, color: "#a0aec0" }}
                      title={row.email}
                    >
                      {row.email}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...bCell, minWidth: 80 }}>
                    <Typography sx={txt} title={row.church}>
                      {row.church}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 40 }}>
                    <Typography sx={txt}>{row.age}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 90 }}>
                    <Typography sx={txt}>{row.location}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 70 }}>
                    <Typography sx={txt}>{row.participated}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 70 }}>
                    <Typography sx={txt}>{row.connected}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 55 }}>
                    <Typography sx={txt}>{row.updates}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...bCell, minWidth: 100 }}>
                    {paymentUpdatingId === row.id ? (
                      <CircularProgress size={14} sx={{ color: "#ef2d2d" }} />
                    ) : (
                      <Select
                        value={row.payment_status.toLowerCase()}
                        size="small"
                        onChange={(e) => handlePaymentChange(row.id, e.target.value)}
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          height: "26px",
                          minWidth: 90,
                          borderRadius: "6px",
                          backgroundColor: STATUS_COLORS[row.payment_status.toLowerCase()]?.bg || "#f0f0f0",
                          color: STATUS_COLORS[row.payment_status.toLowerCase()]?.color || "#333",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& .MuiSelect-icon": {
                            color: STATUS_COLORS[row.payment_status.toLowerCase()]?.color || "#333",
                            fontSize: 16,
                          },
                          "& .MuiSelect-select": { py: "3px", pl: "8px", pr: "24px !important" },
                        }}
                      >
                        <MenuItem value="pending" sx={{ fontSize: 12 }}>Pending</MenuItem>
                        <MenuItem value="verified" sx={{ fontSize: 12 }}>Verified</MenuItem>
                        <MenuItem value="rejected" sx={{ fontSize: 12 }}>Rejected</MenuItem>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bCell, width: 40, textAlign: "right" }}>
                    {exportingId === row.id ? (
                      <CircularProgress size={14} sx={{ color: "#ef2d2d" }} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                        sx={{ p: "3px" }}
                      >
                        <MoreVertOutlinedIcon
                          sx={{ fontSize: 16, color: "#a0aec0" }}
                        />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <ActionMenu />
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1.5,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#a0aec0" }}>
          {filtered.length === 0
            ? "0 - 0 of 0"
            : `${page * rowsPerPage + 1} - ${Math.min(
                (page + 1) * rowsPerPage,
                filtered.length
              )} of ${filtered.length}`}
        </Typography>
        <Box>
          <IconButton size="small" onClick={handlePrev} disabled={page === 0}>
            <KeyboardArrowLeftIcon sx={{ color: "#8b95a1", fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext}
            disabled={(page + 1) * rowsPerPage >= filtered.length}
          >
            <KeyboardArrowRightIcon sx={{ color: "#8b95a1", fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default NewUsers;
